/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { MediaPowderService } from '../../../../../service/MediaPowderServices';
import { useTranslation } from 'react-i18next';
import LogNewShipmentForm from '../../../../common/commonComponents/logNewShipmentForm';

interface PowderLotCreationProps {
  onSave: () => void;
  onCancel: () => void;
  showToastMessage: (message: string, type: string) => void;
}

interface ProductInfo {
  id: string;
  name: string;
  manufacturer: string;
  sku: string;
}


const PowderLotCreation: React.FC<PowderLotCreationProps> = ({ onSave, onCancel, showToastMessage }) => {
  const [productData, setProductData] = useState({ manufacturer: '', sku: '' });
  const [formData, setFormData] = useState({
    selectedProductId: '',
    lotId: '',
    manufactureDate: '',
    expiryDate: '',
    receivedGms: '',
    numContainers: '',
    qtyPerContainer: '0.00',
  });

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [products, setProducts] = useState<ProductInfo[]>([]);
  const { t } = useTranslation(); 
  const [isSaving, setIsSaving] = useState(false);
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };
   

  interface Errors {
    selectedProductId?: string;
    lotId?: string;
    manufactureDate?: string;
    expiryDate?: string;
    receivedGms?: string;
    numContainers?: string;
  }

  const [errors, setErrors] = useState<Errors>({});
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await MediaPowderService.getMediaProductDefinitions();
        if (res?.status && res?.data) {
          const formatted = res.data.map((item: any) => ({
            id: item.id,
            name: item.media_product_name,
            manufacturer: item.manufacturer_name,
            sku: item.catalog_number,
          }));
          setProducts(formatted);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products", error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);


  // Update manufacturer and SKU when a product is selected
  useEffect(() => {
    const product = products.find(p => p.id === formData.selectedProductId);
    if (product) {
      setProductData({
        manufacturer: product.manufacturer,
        sku: product.sku,
      });
    } else {
      setProductData({ manufacturer: '', sku: '' });
    }
  }, [formData.selectedProductId, products]);


  // Auto-calculate quantity per container based on total and count
  useEffect(() => {
    const total = parseFloat(formData.receivedGms.toString());
    const count = parseFloat(formData.numContainers.toString());

    if (!isNaN(total) && !isNaN(count) && count > 0) {
      setFormData(prev => ({
        ...prev,
        qtyPerContainer: (total / count).toFixed(2),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        qtyPerContainer: '0.00',
      }));
    }
  }, [formData.receivedGms, formData.numContainers]);


  
  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData.selectedProductId) newErrors.selectedProductId = t("mediaPowder.errors.product");
    if (!formData.lotId) newErrors.lotId = t("mediaPowder.errors.lotId");
    if (!formData.manufactureDate) newErrors.manufactureDate = t("mediaPowder.errors.manufactureDate");
    if (!formData.expiryDate) newErrors.expiryDate = t("mediaPowder.errors.expiryDate");
    if (!formData.receivedGms) {
        newErrors.receivedGms = t("mediaPowder.errors.receivedGms");
    } else if (Number(formData.receivedGms) <= 0) {
        newErrors.receivedGms = t('mediaPowder.errors.quantity');
    }
    if (!formData.numContainers) {
        newErrors.numContainers = t("mediaPowder.errors.numContainers");
    } else if (
        Number(formData.numContainers) <= 0 ||
        Number(formData.numContainers) > Number(formData.numContainers)) 
    {
        newErrors.numContainers =t('mediaPowder.errors.containerValidation');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };


  // Save handler to create new powder lot
  const handleSave = async () => {
    if (isSaving) return;
    if (!validateForm()) return;

    const payload = {
      media_product_name_id: Number(formData.selectedProductId),
      manufacturer_lot_number: formData.lotId,
      total_quantity_received: Number(formData.receivedGms),
      manufacturing_date: formData.manufactureDate,
      expiry_date: formData.expiryDate,
      no_of_container: Number(formData.numContainers),
      quantity_per_container: Number(formData.qtyPerContainer),
    };

    try {
      setIsSaving(true);
      const res = await MediaPowderService.createMediaPowder(payload);
      if (res?.status) {
        showToastMessage(res?.message, "success");
        onSave();
        return;
      } else {
        showToastMessage(res?.message, "error");
      }
    } catch (error) {
      showToastMessage(t("mediaPowder.errors.errorSaving"), "error");
    }
    finally {
      setIsSaving(false);
    }
  };


  return (
  <LogNewShipmentForm
    formData={formData}
      productData={productData}
      products={products}
      loadingProducts={loadingProducts}
      errors={errors}
      onChange={handleChange}
      onSave={handleSave}
      isSaving={isSaving}
      onCancel={onCancel}
      translationKey="mediaPowder"
    />
  );
};

export default PowderLotCreation;
